import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck, Plus, Search, SlidersHorizontal } from "lucide-react";
import InterviewList from "./InterviewList";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import { PageHeader, AppButton, FilterModal, FilterGroup } from "../ui";
import "./interviews.scss";

const PAGE_SIZE = 10;

const InterviewsPage = () => {
  const [filters, setFilters] = useState({
    instructorName: "",
    currentStatus: "",
    search: "",
  });
  const [searchInput, setSearchInput] = useState("");
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [instructorOptions, setInstructorOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInterviews = async () => {
      setLoading(true);
      try {
        const params = {
          page,
          pageSize,
          ...(filters.instructorName && { instructorName: filters.instructorName }),
          ...(filters.currentStatus && { currentStatus: filters.currentStatus }),
          ...(filters.search && { search: filters.search }),
        };
        const { data } = await apiClient.get("/interviews/list", { params });
        setInterviews(data.interviews || []);
        setTotal(data.total || 0);
        setInstructorOptions((data.filters?.instructors || []).map(i => ({
          value: i._id,
          label: i.name,
        })));
        setStatusOptions(data.filters?.currentStatus || []);
      } catch {
        setToast({ show: true, message: "Failed to load interviews.", type: "error" });
      }
      setLoading(false);
    };
    fetchInterviews();
  }, [filters, page, pageSize]);

  useEffect(() => { setPage(1); }, [filters]);

  const handleSearchBlur = () => {
    if (searchInput !== filters.search) {
      setFilters(f => ({ ...f, search: searchInput }));
    }
  };

  const activeFilterCount = [filters.instructorName, filters.currentStatus].filter(Boolean).length;

  const handleResetFilters = () => {
    setFilters(f => ({ ...f, instructorName: "", currentStatus: "" }));
    setShowFilterModal(false);
  };

  return (
    <div className="iv-page">
      <PageHeader
        title="Interviews"
        description={`Manage ${total} candidate interviews`}
        icon={CalendarCheck}
        actions={
          <AppButton onClick={() => navigate("/interviews/new")}>
            <Plus size={16} />
            <span>Schedule Interview</span>
          </AppButton>
        }
      />

      {/* Toolbar */}
      <div className="iv-toolbar">
        <div className="iv-search">
          <Search size={16} className="iv-search__icon" />
          <input
            type="text"
            className="iv-search__input"
            placeholder="Search students..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onBlur={handleSearchBlur}
            onKeyDown={e => e.key === "Enter" && handleSearchBlur()}
          />
        </div>

        {/* Desktop inline filters */}
        <select
          className="iv-filter-select d-none d-md-block"
          value={filters.currentStatus}
          onChange={e => setFilters(f => ({ ...f, currentStatus: e.target.value }))}
        >
          <option value="">All Status</option>
          {statusOptions.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        <select
          className="iv-filter-select d-none d-md-block"
          value={filters.instructorName}
          onChange={e => setFilters(f => ({ ...f, instructorName: e.target.value }))}
        >
          <option value="">All Interviewers</option>
          {instructorOptions.map(inst => (
            <option key={inst.value} value={inst.value}>{inst.label}</option>
          ))}
        </select>

        {/* Mobile filter button */}
        <button
          className={`iv-filter-btn d-md-none${activeFilterCount > 0 ? " iv-filter-btn--active" : ""}`}
          onClick={() => setShowFilterModal(true)}
        >
          <SlidersHorizontal size={16} />
          {activeFilterCount > 0 && <span className="iv-filter-badge">{activeFilterCount}</span>}
        </button>
      </div>

      {/* Mobile Filter Modal */}
      <FilterModal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        onReset={handleResetFilters}
      >
        <FilterGroup label="Status">
          <select
            className="iv-filter-select"
            value={filters.currentStatus}
            onChange={e => setFilters(f => ({ ...f, currentStatus: e.target.value }))}
          >
            <option value="">All Status</option>
            {statusOptions.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </FilterGroup>
        <FilterGroup label="Interviewer">
          <select
            className="iv-filter-select"
            value={filters.instructorName}
            onChange={e => setFilters(f => ({ ...f, instructorName: e.target.value }))}
          >
            <option value="">All Interviewers</option>
            {instructorOptions.map(inst => (
              <option key={inst.value} value={inst.value}>{inst.label}</option>
            ))}
          </select>
        </FilterGroup>
      </FilterModal>

      <InterviewList
        data={interviews}
        loading={loading}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        total={total}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(t => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default InterviewsPage;
